import type {
  ArtifactUploadResult,
  PaginatedUpdateList,
  ProductionDeploymentSummary,
  ProductionReleaseDetail,
  ProductionReleaseSummary,
  ProductionUpdateEventSummary,
  ReleaseActionResult,
  ReleaseCreateResult,
  UpdateCodeRevealResult,
  UpdateCodeResult,
  UpdateCustomerSetupResult,
  UpdateDeploymentOperationResult,
} from '@quds/shared';
import { apiDelete, apiGet, apiPatch, apiPost, apiPostForm } from '@/lib/api';

/** Lightweight list row shapes (avoid pulling zod via `@quds/shared/production`). */
export interface UpdateCustomerListItem {
  id: string;
  pharmacyName: string;
  status: string;
  assignedChannel: string;
  deviceCount: number;
  activeDeviceCount: number;
  updatedAt: string;
  hasActiveCode?: boolean;
}

export interface UpdateClientListItem {
  id: string;
  deviceId: string;
  currentVersion: string;
  releaseChannel: string;
  lastUpdateStatus: string | null;
  lastSeenAt: string | null;
}

export const listUpdateReleases = (query: { limit?: number; page?: number; status?: string } = {}) =>
  apiGet<PaginatedUpdateList<ProductionReleaseSummary>>('/updates/releases', query).then((res) => ({
    ...res,
    // Back-compat for deploy modal which only needs items + requestId
    items: res.items,
    requestId: res.requestId,
  }));

export const getUpdateRelease = (id: string) =>
  apiGet<ProductionReleaseDetail & { requestId: string }>(`/updates/releases/${encodeURIComponent(id)}`);

export const createUpdateRelease = (body: {
  version: string;
  channel: 'stable' | 'rc' | 'beta';
  productKey?: string;
  releaseNotes?: string;
  codeSigned?: boolean;
  idempotencyKey: string;
}) => apiPost<ReleaseCreateResult>('/updates/releases', body);

export const patchUpdateRelease = (id: string, body: Record<string, unknown> & { idempotencyKey: string }) =>
  apiPatch<ReleaseActionResult>(`/updates/releases/${encodeURIComponent(id)}`, body);

export const validateUpdateRelease = (id: string, body: { idempotencyKey: string }) =>
  apiPost<ReleaseActionResult>(`/updates/releases/${encodeURIComponent(id)}/validate`, body);

export const publishUpdateRelease = (id: string, body: { idempotencyKey: string }) =>
  apiPost<ReleaseActionResult>(`/updates/releases/${encodeURIComponent(id)}/publish`, body);

export const deleteDraftUpdateRelease = (id: string, body: { idempotencyKey: string }) =>
  apiDelete<ReleaseActionResult>(`/updates/releases/${encodeURIComponent(id)}`, body);

export const uploadUpdateReleaseArtifact = (
  id: string,
  file: File,
  options: { idempotencyKey: string; artifactType?: string },
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('idempotencyKey', options.idempotencyKey);
  if (options.artifactType) form.append('artifactType', options.artifactType);
  return apiPostForm<ArtifactUploadResult>(`/updates/releases/${encodeURIComponent(id)}/artifacts`, form);
};

export const listUpdateDeployments = (query: { page?: number; limit?: number } = {}) =>
  apiGet<PaginatedUpdateList<ProductionDeploymentSummary>>('/updates/deployments', query);

export const updateDeploymentAction = (
  id: string,
  action: 'activate' | 'pause' | 'resume' | 'cancel',
  body: { idempotencyKey: string },
) => apiPost<UpdateDeploymentOperationResult>(`/updates/deployments/${encodeURIComponent(id)}/${action}`, body);

export const listUpdateCustomersGlobal = (query: { q?: string; status?: string; limit?: number; cursor?: string } = {}) =>
  apiGet<{ items: UpdateCustomerListItem[]; nextCursor: string | null; requestId: string }>('/updates/customers', query);

export const listUpdateClientsGlobal = (query: { limit?: number; cursor?: string; productKey?: string } = {}) =>
  apiGet<{ items: UpdateClientListItem[]; nextCursor: string | null; requestId: string }>('/updates/clients', query);

export const listUpdateEventsGlobal = (query: { page?: number; limit?: number; failuresOnly?: boolean; eventType?: string } = {}) =>
  apiGet<PaginatedUpdateList<ProductionUpdateEventSummary>>('/updates/events', query);

export const setupBusinessUpdates = (
  businessId: string,
  body: {
    pharmacyName?: string;
    assignedChannel?: 'stable' | 'rc' | 'beta';
    generateCode?: boolean;
    idempotencyKey: string;
  },
) => apiPost<UpdateCustomerSetupResult>(`/businesses/${encodeURIComponent(businessId)}/updates/setup`, body);

export const generateBusinessUpdateCode = (businessId: string, body: { idempotencyKey: string }) =>
  apiPost<UpdateCodeResult>(`/businesses/${encodeURIComponent(businessId)}/updates/code/generate`, body);

export const regenerateBusinessUpdateCode = (businessId: string, body: { idempotencyKey: string }) =>
  apiPost<UpdateCodeResult>(`/businesses/${encodeURIComponent(businessId)}/updates/code/regenerate`, body);

export const revealBusinessUpdateCode = (businessId: string, body: { idempotencyKey: string }) =>
  apiPost<UpdateCodeRevealResult>(`/businesses/${encodeURIComponent(businessId)}/updates/code/reveal`, body);
